import React, { useState, useEffect, useCallback } from 'react';
import {
    Loader, RefreshCw, Download, X,
    Clock, CheckCircle, XCircle, AlertCircle, Grid3x3, RotateCcw
} from 'lucide-react';
import api from '../../api/api';
import { useTheme } from '../../hooks/useTheme';

const STATUS_STYLES = {
    pending:  { badge: 'bg-yellow-100 text-yellow-700 border border-yellow-200',   icon: Clock,        label: 'Pending'  },
    approved: { badge: 'bg-primary-100 text-primary-700 border border-primary-200', icon: CheckCircle,  label: 'Approved' },
    rejected: { badge: 'bg-red-100 text-red-600 border border-red-200',             icon: XCircle,      label: 'Rejected' },
};

export default function EmployerImportsTab() {
    const { isDarkMode } = useTheme();

    const [submissions, setSubmissions]     = useState([]);
    const [loading, setLoading]             = useState(true);
    const [error, setError]                 = useState(null);
    const [actionLoading, setActionLoading] = useState(null);

    // Grid preview modal: { open, rows, title }
    const [gridModal, setGridModal] = useState({ open: false, rows: [], title: '' });

    // ── Fetch ─────────────────────────────────────────────────────────────────
    const fetchSubmissions = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await api.get('/jobs/import/pending');
            setSubmissions(res.data.submissions ?? []);
        } catch (err) {
            console.error('Failed to fetch employer submissions:', err);
            setError(err.response?.data?.error || 'Failed to load submissions.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchSubmissions(); }, [fetchSubmissions]);

    // ── Download ──────────────────────────────────────────────────────────────
    const handleDownload = async (id, employerName) => {
        try {
            const res = await api.get(`/jobs/import/pending/${id}/download`, { responseType: 'blob' });
            const url  = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href  = url;
            link.setAttribute('download', `${employerName}_submission_${id}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Download failed:', err);
            alert('Download failed. Please try again.');
        }
    };

    // ── Approve / Reject ──────────────────────────────────────────────────────
    const handleApprove = async (id) => {
        setActionLoading(id);
        try {
            await api.post(`/jobs/import/pending/${id}/approve`);
            await fetchSubmissions();
        } catch (err) {
            console.error('Approve failed:', err);
            alert(err.response?.data?.error || 'Approve failed. Please try again.');
        } finally {
            setActionLoading(null);
        }
    };

    const handleReject = async (id) => {
        setActionLoading(id);
        try {
            await api.post(`/jobs/import/pending/${id}/reject`);
            await fetchSubmissions();
        } catch (err) {
            console.error('Reject failed:', err);
            alert(err.response?.data?.error || 'Reject failed. Please try again.');
        } finally {
            setActionLoading(null);
        }
    };

    const handleRevert = async (id) => {
        setActionLoading(id);
        try {
            await api.post(`/jobs/import/pending/${id}/revert`);
            await fetchSubmissions();
        } catch (err) {
            console.error('Revert failed:', err);
            alert(err.response?.data?.error || 'Revert failed. Please try again.');
        } finally {
            setActionLoading(null);
        }
    };

    // ── Grid modal ────────────────────────────────────────────────────────────
    const openGrid = (sub) => {
        let rows = [];
        try { rows = JSON.parse(sub.ParsedData ?? '[]'); } catch {}
        setGridModal({
            open: true,
            rows,
            title: `${sub.EmployerName || 'Submission'} — ${rows.length} job${rows.length !== 1 ? 's' : ''}`,
        });
    };
    const closeGrid = () => setGridModal({ open: false, rows: [], title: '' });

    // ── Style helpers ─────────────────────────────────────────────────────────
    const card    = `rounded-2xl border shadow-sm ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`;
    const th      = `px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`;
    const td      = `px-4 py-3 text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`;
    const rowBase = `border-b transition-colors ${isDarkMode ? 'border-slate-700 hover:bg-slate-700/30' : 'border-slate-100 hover:bg-slate-50'}`;
    const gridTh  = `px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide sticky top-0 ${isDarkMode ? 'bg-slate-700 text-slate-400' : 'bg-slate-50 text-slate-500'}`;
    const gridTd  = `px-4 py-2.5 text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`;
    const gridRow = `border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-100'}`;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className={`text-xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                        Employer Job Submissions
                    </h2>
                    <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        Review Excel files submitted by employers for approval.
                    </p>
                </div>
                <button
                    onClick={fetchSubmissions}
                    disabled={loading}
                    className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg border transition-colors disabled:opacity-50
                        ${isDarkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}
                >
                    <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
                    Refresh
                </button>
            </div>

            {/* Error banner */}
            {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                    <AlertCircle size={18} className="text-red-600 flex-shrink-0" />
                    <p className="text-sm text-red-700">{error}</p>
                </div>
            )}

            {/* Loading */}
            {loading && (
                <div className={`${card} p-12 flex flex-col items-center gap-3`}>
                    <Loader className="animate-spin text-primary-500" size={28} />
                    <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Loading submissions...</p>
                </div>
            )}

            {/* Empty state */}
            {!loading && !error && submissions.length === 0 && (
                <div className={`${card} p-12 text-center`}>
                    <Grid3x3 size={36} className={`mx-auto mb-3 ${isDarkMode ? 'text-slate-600' : 'text-slate-300'}`} />
                    <p className={`font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>No submissions yet</p>
                    <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Employer submissions will appear here for review.</p>
                </div>
            )}

            {/* Submissions table */}
            {!loading && submissions.length > 0 && (
                <div className={`${card} overflow-hidden`}>
                    <table className="w-full">
                        <thead>
                            <tr className={`border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                                <th className={th}>Employer</th>
                                <th className={th}>Contact</th>
                                <th className={th}>Submitted</th>
                                <th className={th}>Rows</th>
                                <th className={th}>Status</th>
                                <th className={`${th} text-right`}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {submissions.map((sub) => {
                                const statusStyle = STATUS_STYLES[sub.Status] ?? STATUS_STYLES.pending;
                                const StatusIcon  = statusStyle.icon;

                                return (
                                    <tr key={sub.PendingImportID} className={rowBase}>
                                        {/* Employer */}
                                        <td className={`${td} font-medium`}>
                                            {sub.EmployerName || '—'}
                                            <div className={`text-xs mt-0.5 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                                ID #{sub.SubmittedBy}
                                            </div>
                                        </td>

                                        {/* Contact */}
                                        <td className={td}>
                                            <div className="font-medium">
                                                {[sub.FirstName, sub.LastName].filter(Boolean).join(' ') || '—'}
                                            </div>
                                            <div className={`text-xs mt-0.5 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                                {sub.Email || '—'}
                                            </div>
                                        </td>

                                        {/* Submitted date */}
                                        <td className={td}>
                                            {sub.SubmittedOn
                                                ? new Date(sub.SubmittedOn).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                                : '—'}
                                            <div className={`text-xs mt-0.5 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                                {sub.SubmittedOn
                                                    ? new Date(sub.SubmittedOn).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                                                    : ''}
                                            </div>
                                        </td>

                                        {/* Row count */}
                                        <td className={td}>{sub.TotalRows ?? '—'}</td>

                                        {/* Status badge */}
                                        <td className={td}>
                                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusStyle.badge}`}>
                                                <StatusIcon size={12} />
                                                {statusStyle.label}
                                            </span>
                                        </td>

                                        {/* Actions */}
                                        <td className="px-4 py-3">
                                            <div className="flex items-center justify-end gap-2">
                                                {/* Approve / Reject — pending only */}
                                                {sub.Status === 'pending' && (
                                                    <>
                                                        <button
                                                            onClick={() => handleApprove(sub.PendingImportID)}
                                                            disabled={actionLoading === sub.PendingImportID}
                                                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-primary-300 text-primary-700 hover:bg-primary-50 transition-colors disabled:opacity-50"
                                                        >
                                                            {actionLoading === sub.PendingImportID
                                                                ? <Loader size={13} className="animate-spin" />
                                                                : <CheckCircle size={13} />}
                                                            Approve
                                                        </button>
                                                        <button
                                                            onClick={() => handleReject(sub.PendingImportID)}
                                                            disabled={actionLoading === sub.PendingImportID}
                                                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-red-300 text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                                                        >
                                                            {actionLoading === sub.PendingImportID
                                                                ? <Loader size={13} className="animate-spin" />
                                                                : <XCircle size={13} />}
                                                            Reject
                                                        </button>
                                                    </>
                                                )}

                                                {/* Revert to Pending — approved/rejected only */}
                                                {sub.Status !== 'pending' && (
                                                    <button
                                                        onClick={() => handleRevert(sub.PendingImportID)}
                                                        disabled={actionLoading === sub.PendingImportID}
                                                        className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors disabled:opacity-50
                                                            ${isDarkMode
                                                                ? 'border-slate-600 text-slate-300 hover:bg-slate-700'
                                                                : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}
                                                    >
                                                        {actionLoading === sub.PendingImportID
                                                            ? <Loader size={13} className="animate-spin" />
                                                            : <RotateCcw size={13} />}
                                                        Revert to Pending
                                                    </button>
                                                )}

                                                {/* View Grid — opens popup */}
                                                <button
                                                    onClick={() => openGrid(sub)}
                                                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors
                                                        ${isDarkMode
                                                            ? 'border-slate-600 text-slate-300 hover:bg-slate-700'
                                                            : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}
                                                >
                                                    <Grid3x3 size={13} />
                                                    View Grid
                                                </button>

                                                {/* Download */}
                                                <button
                                                    onClick={() => handleDownload(sub.PendingImportID, sub.EmployerName)}
                                                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors
                                                        ${isDarkMode
                                                            ? 'border-slate-600 text-slate-300 hover:bg-slate-700'
                                                            : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}
                                                >
                                                    <Download size={13} />
                                                    Download
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* ── Grid Preview Modal ─────────────────────────────────────────── */}
            {gridModal.open && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={closeGrid}>
                    <div
                        className={`rounded-2xl shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Modal header */}
                        <div className={`flex items-center justify-between px-6 py-4 border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                            <div>
                                <h3 className={`font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                                    Job Submission Preview
                                </h3>
                                <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                    {gridModal.title}
                                </p>
                            </div>
                            <button
                                onClick={closeGrid}
                                className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}
                            >
                                <X size={18} />
                            </button>
                        </div>

                        {/* Modal body */}
                        <div className="overflow-auto flex-1">
                            {gridModal.rows.length === 0 ? (
                                <p className={`px-6 py-12 text-sm text-center ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                    No row data available.
                                </p>
                            ) : (
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className={isDarkMode ? 'border-b border-slate-700' : 'border-b border-slate-200'}>
                                            <th className={gridTh}>#</th>
                                            <th className={gridTh}>Job Title</th>
                                            <th className={gridTh}>Company</th>
                                            <th className={gridTh}>Location</th>
                                            <th className={gridTh}>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {gridModal.rows.map((r, idx) => (
                                            <tr key={idx} className={gridRow}>
                                                <td className={`${gridTd} font-mono text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                                    {r.row ?? idx + 1}
                                                </td>
                                                <td className={`${gridTd} font-medium ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                                                    {r.title || '—'}
                                                </td>
                                                <td className={gridTd}>{r.company || '—'}</td>
                                                <td className={gridTd}>{r.location || '—'}</td>
                                                <td className={gridTd}>
                                                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                                                        r.status === 'valid'
                                                            ? 'bg-primary-100 text-primary-700'
                                                            : 'bg-red-100 text-red-600'
                                                    }`}>
                                                        {r.status}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        {/* Modal footer */}
                        <div className={`px-6 py-3 border-t flex justify-end ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                            <button
                                onClick={closeGrid}
                                className={`px-4 py-2 text-sm rounded-lg border transition-colors
                                    ${isDarkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
